import React, { useState, useEffect, useRef } from "react";
import { Editor } from "react-draft-wysiwyg";
import { EditorState, ContentState, convertToRaw } from "draft-js";
import htmlToDraft from "html-to-draftjs";
import draftToHtml from "draftjs-to-html";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";

const RichTextEditor = ({
  value = "",
  onChange,
  placeholder = "Enter content here...",
  readOnly = false,
  minHeight = "150px",
  onContentImgUpload,
  onRichtextStateChange,
  ...props
}) => {
  const [editorState, setEditorState] = useState(EditorState.createEmpty());
  const [contentImgs, setContentImgs] = useState({});
  const isInternalChange = useRef(false);
  const previousValue = useRef("");
  const isInitialized = useRef(false);
  const editorRef = useRef(null);

  useEffect(() => {
    if (onContentImgUpload) {
      onContentImgUpload(contentImgs);
    }
  }, [contentImgs]);

  // Initialize editor state when value changes externally
  useEffect(() => {
    // Always load initial content on first render, or when external changes occur
    if (
      !isInitialized.current ||
      (!isInternalChange.current && value !== previousValue.current)
    ) {
      if (
        value &&
        value !== "<p></p>" &&
        value !== "<p><br></p>" &&
        value.trim() !== ""
      ) {
        try {
          const blocksFromHtml = htmlToDraft(`<p>${value}</p>`);
          const { contentBlocks, entityMap } = blocksFromHtml;
          const contentState = ContentState.createFromBlockArray(
            contentBlocks,
            entityMap
          );
          setEditorState(EditorState.createWithContent(contentState));
        } catch (error) {
          console.warn("Error parsing HTML content:", error);
          setEditorState(EditorState.createEmpty());
        }
      } else {
        setEditorState(EditorState.createEmpty());
      }
      isInitialized.current = true;
    }

    // Reset the flag and update previous value
    isInternalChange.current = false;
    previousValue.current = value;
  }, [value]);

  const handleEditorStateChange = (newEditorState) => {
    setEditorState(newEditorState);
    if (onRichtextStateChange) {
      onRichtextStateChange(newEditorState);
    }
    isInternalChange.current = true; // Mark as internal change
    console.log('newEditorState', newEditorState)
    const contentState = newEditorState.getCurrentContent();
    console.log('contentState', contentState)
    const rawContentState = convertToRaw(contentState);
    console.log('rawContentState', rawContentState)
    let content = draftToHtml(rawContentState);
    console.log('content', content)
    if (onChange) {
      content = content
        .replace(/<p><br><\/p>/g, "")
        .replace(/<p><\/p>/g, "")
        .replace(/\n/g, "");
      onChange(content);
    }
  };

  const uploadImageCallBack = (file) => {
    return new Promise((resolve, reject) => {
      // Create a local URL for the preview
      const localSrc = URL.createObjectURL(file);
      const tempId = `temp-${Date.now()}`;

      // Resolve the promise with the localSrc for immediate display
      resolve({ data: { link: `${localSrc}#tempId=${tempId}` } });
      setContentImgs((prev) => ({ ...prev, [tempId]: { file, localSrc } }));
    });
  };

  const toolbarConfig = {
    options: [
      "inline",
      "blockType",
      "fontSize",
      "list",
      "textAlign",
      "colorPicker",
      "link",
      "history",
      "image",
      "embedded",
      "emoji",
    ],
    inline: {
      inDropdown: true,
      options: ["bold", "italic", "underline", "strikethrough"],
    },
    blockType: {
      inDropdown: true,
      options: ["Normal", "H1", "H2", "H3", "H4", "H5", "H6", "Blockquote"],
    },
    fontSize: {
      options: [8, 9, 10, 11, 12, 14, 16, 18, 24, 30, 36, 48, 60, 72, 96],
    },
    list: {
      inDropdown: true,
      options: ["unordered", "ordered"],
    },
    textAlign: {
      inDropdown: true,
    },
    link: {
      inDropdown: true,
    },
    history: {
      inDropdown: true,
    },
    image: {
      uploadCallback: uploadImageCallBack,
      previewImage: true,
      alt: { present: true, mandatory: false },
      defaultSize: {
        height: "auto",
        width: "100%",
      },
    },
    embedded: {
      inDropdown: true,
      embedCallback: (link) => {
        // Convert regular YouTube URLs to embed format
        let embedUrl = link;
        
        // Handle youtube.com/watch?v=VIDEO_ID format
        const youtubeWatchMatch = link.match(/(?:youtube\.com\/watch\?v=|youtube\.com\/watch\?.*&v=)([^&]+)/);
        if (youtubeWatchMatch) {
          embedUrl = `https://www.youtube.com/embed/${youtubeWatchMatch[1]}`;
        }
        
        // Handle youtu.be/VIDEO_ID format
        const youtubeShortMatch = link.match(/youtu\.be\/([^?]+)/);
        if (youtubeShortMatch) {
          embedUrl = `https://www.youtube.com/embed/${youtubeShortMatch[1]}`;
        }
        
        // Handle youtube.com/embed/VIDEO_ID (already in embed format)
        // No conversion needed, just return as is
        
        return embedUrl;
      },
      defaultSize: {
        height: '315',
        width: '560',
      },
    },
    emoji: {
      inDropdown: true,
    },
  };

  return (
    <div ref={editorRef} style={{ position: "relative", zIndex: 1400 }}>
      <style>
        {`
          .rdw-editor-wrapper .rdw-dropdown-wrapper,
          .rdw-editor-wrapper .rdw-dropdown-optionwrapper {
            z-index: 1500 !important;
          }
          .rdw-editor-wrapper .rdw-colorpicker-modal,
          .rdw-editor-wrapper .rdw-link-modal,
          .rdw-editor-wrapper .rdw-embedded-modal,
          .rdw-editor-wrapper .rdw-emoji-modal,
          .rdw-editor-wrapper .rdw-image-modal {
            z-index: 1500 !important;
          }
          .rdw-editor-main iframe {
            max-width: 100%;
          }
        `}
      </style>
      <Editor
        editorState={editorState}
        onEditorStateChange={handleEditorStateChange}
        wrapperClassName="rdw-editor-wrapper"
        editorClassName="rdw-editor-main"
        toolbar={toolbarConfig}
        placeholder={placeholder}
        readOnly={readOnly}
        wrapperStyle={{
          border: "1px solid #f1f1f1",
          borderRadius: "4px",
          minHeight: "200px",
          padding: "10px",
          overflow: "auto",
          ...props.wrapperStyle,
        }}
        editorStyle={{
          minHeight,
          padding: "10px",
          borderTop: "1px solid #f1f1f1",
          ...props.editorStyle,
        }}
        toolbarStyle={{
          borderBottom: "1px solid #f1f1f1",
          marginBottom: "5px",
          ...props.toolbarStyle,
        }}
        {...props}
      />
    </div>
  );
};

export default RichTextEditor;
